/**
 * Writing to the work hierarchy: one form dialog for all four levels, and one
 * confirmation for the delete that takes a subtree with it.
 *
 * ## Why one form rather than four
 *
 * The four levels differ by about three fields each. Four components would be
 * four places to forget `aria-describedby`, four places for the slug helper to
 * drift, and four places to get "blank means null" wrong. So the shape of each
 * level is data (`SPECS`) and the rendering is shared.
 *
 * ## Validation is the shared schema, not a second opinion
 *
 * Every submit runs the exact `createXSchema` / `updateXSchema` the API runs.
 * The form is not deciding what is valid - it is running the same decision
 * early so the answer arrives in the field rather than as a 400.
 *
 * ## `queries.ts` has no mutation hooks for these levels
 *
 * It has them for tasks, time entries and users. Rather than edit a file this
 * agent does not own, the four levels are wrapped here, invalidating the same
 * caches a task write does - a cascade delete removes tasks, which moves the
 * board, the rollups, capacity and the client dashboard all at once.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import type { FormEvent } from 'react';
import { useState } from 'react';
import type { z } from 'zod';

import {
  createApplicationSchema,
  createClientSchema,
  createFeatureSchema,
  createProjectSchema,
  updateApplicationSchema,
  updateClientSchema,
  updateFeatureSchema,
  updateProjectSchema,
} from '@botguide/shared';

import { Button, Dialog, ErrorNote, Field, Textarea } from '@/components';
import { applicationsApi, clientsApi, featuresApi, projectsApi } from '@/lib/api';
import { formatCount } from '@/lib/format';
import { queryKeys } from '@/lib/queries';

import type { CreateTarget, EntityKind, TreeTarget } from './EntityTree';
import { ENTITY_LABELS, entityOf, nameOf, totalsFor } from './EntityTree';

/* -------------------------------------------------------------------------- */
/* Mutations                                                                  */
/* -------------------------------------------------------------------------- */

type Body<S extends z.ZodTypeAny> = z.input<S>;

export type SaveRequest =
  | { mode: 'create'; kind: 'client'; body: Body<typeof createClientSchema> }
  | { mode: 'create'; kind: 'application'; body: Body<typeof createApplicationSchema> }
  | { mode: 'create'; kind: 'project'; body: Body<typeof createProjectSchema> }
  | { mode: 'create'; kind: 'feature'; body: Body<typeof createFeatureSchema> }
  | { mode: 'update'; kind: 'client'; id: string; body: Body<typeof updateClientSchema> }
  | { mode: 'update'; kind: 'application'; id: string; body: Body<typeof updateApplicationSchema> }
  | { mode: 'update'; kind: 'project'; id: string; body: Body<typeof updateProjectSchema> }
  | { mode: 'update'; kind: 'feature'; id: string; body: Body<typeof updateFeatureSchema> };

/**
 * Anything written here can move any screen derived from tasks, because a
 * delete cascades all the way down. Invalidating the seven is cheap; a board
 * still showing the tasks of a deleted project is a support ticket.
 */
function useHierarchyInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    for (const key of [
      queryKeys.clients.all,
      queryKeys.applications.all,
      queryKeys.projects.all,
      queryKeys.features.all,
      queryKeys.tasks.all,
      queryKeys.board.all,
      queryKeys.capacity.all,
      queryKeys.dashboard.all,
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

async function save(request: SaveRequest): Promise<void> {
  if (request.mode === 'create') {
    switch (request.kind) {
      case 'client':
        await clientsApi.create(request.body);
        return;
      case 'application':
        await applicationsApi.create(request.body);
        return;
      case 'project':
        await projectsApi.create(request.body);
        return;
      case 'feature':
        await featuresApi.create(request.body);
        return;
    }
  }

  switch (request.kind) {
    case 'client':
      await clientsApi.update(request.id, request.body);
      return;
    case 'application':
      await applicationsApi.update(request.id, request.body);
      return;
    case 'project':
      await projectsApi.update(request.id, request.body);
      return;
    case 'feature':
      await featuresApi.update(request.id, request.body);
      return;
  }
}

export function useSaveEntity(): UseMutationResult<void, Error, SaveRequest> {
  const invalidate = useHierarchyInvalidation();
  return useMutation({ mutationFn: save, onSuccess: invalidate });
}

const REMOVERS: Record<EntityKind, (id: string) => Promise<void>> = {
  client: clientsApi.remove,
  application: applicationsApi.remove,
  project: projectsApi.remove,
  feature: featuresApi.remove,
};

export function useDeleteEntity(): UseMutationResult<void, Error, TreeTarget> {
  const invalidate = useHierarchyInvalidation();
  return useMutation({
    mutationFn: (target: TreeTarget) => REMOVERS[target.kind](entityOf(target).id),
    onSuccess: invalidate,
  });
}

/* -------------------------------------------------------------------------- */
/* Field specs                                                                */
/* -------------------------------------------------------------------------- */

interface FieldSpec {
  name: string;
  label: string;
  control: 'text' | 'textarea' | 'date';
  required?: boolean;
  /** Blank submits as `null`, not as an empty string - the schemas differ. */
  nullable?: boolean;
  hint?: string;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
}

interface StatusSpec {
  name: 'isActive' | 'isArchived';
  label: string;
  hint: string;
  /** The value when the field is absent from the DTO, which cannot happen. */
  fallback: boolean;
}

interface EntitySpec {
  fields: readonly FieldSpec[];
  /** Offered on edit only. Archiving is the non-destructive alternative. */
  status?: StatusSpec;
  /** The parent id sent on create. Absent for a client, which has no parent. */
  parentField?: 'clientId' | 'applicationId' | 'projectId';
  parentLabel?: string;
}

const NAME: FieldSpec = { name: 'name', label: 'Name', control: 'text', required: true };

const SUMMARY: FieldSpec = {
  name: 'summary',
  label: 'Summary',
  control: 'textarea',
  maxLength: 5000,
  rows: 3,
};

const SLUG: FieldSpec = {
  name: 'slug',
  label: 'Slug',
  control: 'text',
  required: true,
  maxLength: 60,
  hint: 'Lowercase letters, numbers and hyphens. Derived from the name until you change it.',
};

const SPECS: Record<EntityKind, EntitySpec> = {
  client: {
    fields: [NAME, SLUG, SUMMARY],
    status: {
      name: 'isActive',
      label: 'Active',
      hint: 'An inactive client stays in the ledger but is out of the way.',
      fallback: true,
    },
  },
  application: {
    parentField: 'clientId',
    parentLabel: 'Client',
    fields: [
      NAME,
      SLUG,
      SUMMARY,
      {
        name: 'repoUrl',
        label: 'Repository',
        control: 'text',
        nullable: true,
        placeholder: 'https://github.com/…',
      },
      {
        name: 'productionUrl',
        label: 'Production URL',
        control: 'text',
        nullable: true,
        placeholder: 'https://…',
      },
    ],
    status: {
      name: 'isActive',
      label: 'Active',
      hint: 'Inactive applications keep their spend history and stop taking new work.',
      fallback: true,
    },
  },
  project: {
    parentField: 'applicationId',
    parentLabel: 'Application',
    fields: [
      NAME,
      SUMMARY,
      { name: 'startsOn', label: 'Starts on', control: 'date', nullable: true },
      { name: 'targetOn', label: 'Target date', control: 'date', nullable: true },
    ],
    status: {
      name: 'isArchived',
      label: 'Archived',
      hint: 'Archiving keeps every task and hour. Deleting does not.',
      fallback: false,
    },
  },
  feature: {
    parentField: 'projectId',
    parentLabel: 'Project',
    fields: [
      NAME,
      SUMMARY,
      {
        name: 'clientOutcome',
        label: 'Client outcome',
        control: 'textarea',
        maxLength: 5000,
        rows: 3,
        hint: 'What the client is told this delivers. It is shown on their dashboard.',
      },
    ],
  },
};

const CREATE_SCHEMAS = {
  client: createClientSchema,
  application: createApplicationSchema,
  project: createProjectSchema,
  feature: createFeatureSchema,
} as const;

const UPDATE_SCHEMAS = {
  client: updateClientSchema,
  application: updateApplicationSchema,
  project: updateProjectSchema,
  feature: updateFeatureSchema,
} as const;

/* -------------------------------------------------------------------------- */
/* The form dialog                                                            */
/* -------------------------------------------------------------------------- */

/** What the page asked for: a new child, or an edit of an existing node. */
export type EntityFormRequest =
  | { mode: 'create'; target: CreateTarget }
  | { mode: 'edit'; target: TreeTarget };

export interface EntityDialogProps {
  request: EntityFormRequest;
  onClose: () => void;
}

/** `Bailey Engineering` -> `bailey-engineering`. Only ever a starting point. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

type Values = Record<string, string>;

/**
 * The DTO as a bag of fields, so the spec-driven form can read a value by
 * name. The four DTOs have no index signature - deliberately - so the widening
 * is explicit here rather than smuggled into each read.
 */
function fieldsOf(target: TreeTarget): Record<string, unknown> {
  return entityOf(target) as unknown as Record<string, unknown>;
}

function initialValues(request: EntityFormRequest, spec: EntitySpec): Values {
  const values: Values = {};
  if (request.mode === 'create') {
    for (const field of spec.fields) values[field.name] = '';
    return values;
  }

  const entity = fieldsOf(request.target);
  for (const field of spec.fields) {
    const current = entity[field.name];
    values[field.name] = typeof current === 'string' ? current : '';
  }
  return values;
}

function initialStatus(request: EntityFormRequest, spec: EntitySpec): boolean {
  if (!spec.status || request.mode === 'create') return spec.status?.fallback ?? false;
  const entity = fieldsOf(request.target);
  const current = entity[spec.status.name];
  return typeof current === 'boolean' ? current : spec.status.fallback;
}

/** One id so the footer's submit button can live outside the `<form>` it submits. */
const FORM_ID = 'entity-form';

export function EntityDialog({ request, onClose }: EntityDialogProps) {
  const kind: EntityKind = request.target.kind;
  const spec = SPECS[kind];
  const editing = request.mode === 'edit';

  const [values, setValues] = useState<Values>(() => initialValues(request, spec));
  const [status, setStatus] = useState<boolean>(() => initialStatus(request, spec));
  const [slugTouched, setSlugTouched] = useState(editing);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useSaveEntity();

  const set = (name: string, value: string) => {
    setValues((current) => {
      const next = { ...current, [name]: value };
      // The slug follows the name until somebody types in it themselves.
      if (name === 'name' && !slugTouched && 'slug' in current) next.slug = slugify(value);
      return next;
    });
    setErrors((current) => {
      if (!(name in current)) return current;
      const { [name]: _cleared, ...rest } = current;
      return rest;
    });
  };

  function buildPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {};

    for (const field of spec.fields) {
      const raw = (values[field.name] ?? '').trim();
      payload[field.name] = field.nullable && raw === '' ? null : raw;
    }

    if (request.mode === 'create' && spec.parentField && request.target.kind !== 'client') {
      payload[spec.parentField] = request.target.parentId;
    }

    if (editing && spec.status) payload[spec.status.name] = status;

    return payload;
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const schema = editing ? UPDATE_SCHEMAS[kind] : CREATE_SCHEMAS[kind];
    const parsed = schema.safeParse(buildPayload());

    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      let general: string | null = null;
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string' && !(path in fieldErrors)) fieldErrors[path] = issue.message;
        else if (path === undefined) general = issue.message;
      }
      setErrors(fieldErrors);
      setFormError(general);
      return;
    }

    /*
     * `parsed.data` is post-default and correct for the kind, but TypeScript
     * cannot relate the runtime `kind` back to one member of the union. The
     * narrowing that matters already happened: the schema chosen by `kind`
     * accepted this body, so the pair is consistent by construction.
     */
    const saveRequest = (
      request.mode === 'edit'
        ? { mode: 'update', kind, id: entityOf(request.target).id, body: parsed.data }
        : { mode: 'create', kind, body: parsed.data }
    ) as SaveRequest;

    mutation.mutate(saveRequest, { onSuccess: onClose });
  }

  const parentName =
    request.mode === 'create' && request.target.kind !== 'client'
      ? request.target.parentName
      : null;

  const title = editing
    ? `Edit ${ENTITY_LABELS[kind].toLowerCase()}`
    : `New ${ENTITY_LABELS[kind].toLowerCase()}`;

  const description =
    request.mode === 'edit'
      ? nameOf(request.target)
      : parentName
        ? `In ${spec.parentLabel?.toLowerCase() ?? 'parent'} ${parentName}`
        : 'A client is the top of the tree. Applications hang off it.';

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            loading={mutation.isPending}
            iconRight="check"
          >
            {editing ? 'Save changes' : `Create ${ENTITY_LABELS[kind].toLowerCase()}`}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        {mutation.error ? <ErrorNote error={mutation.error} /> : null}
        {formError ? (
          <p role="alert" className="text-[0.8125rem] font-medium text-danger-ink">
            {formError}
          </p>
        ) : null}

        {spec.fields.map((field) =>
          field.control === 'textarea' ? (
            <Textarea
              key={field.name}
              label={field.label}
              hint={field.hint}
              error={errors[field.name] ?? null}
              rows={field.rows}
              maxLength={field.maxLength}
              showCount={Boolean(field.maxLength)}
              value={values[field.name] ?? ''}
              onChange={(event) => set(field.name, event.target.value)}
            />
          ) : (
            <Field
              key={field.name}
              label={field.label}
              hint={field.hint}
              required={field.required}
              placeholder={field.placeholder}
              maxLength={field.maxLength}
              type={field.control === 'date' ? 'date' : 'text'}
              // A date input is narrower than the text fields; let it size itself.
              className={field.control === 'date' ? 'sm:max-w-56' : undefined}
              error={errors[field.name] ?? null}
              autoFocus={field.name === 'name'}
              value={values[field.name] ?? ''}
              onChange={(event) => {
                if (field.name === 'slug') setSlugTouched(true);
                set(field.name, event.target.value);
              }}
            />
          ),
        )}

        {editing && spec.status ? (
          <Checkbox
            label={spec.status.label}
            hint={spec.status.hint}
            checked={status}
            onChange={setStatus}
          />
        ) : null}
      </form>
    </Dialog>
  );
}

function Checkbox({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-line bg-sunken px-3 py-2.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-[var(--accent)]"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-[0.8125rem] text-muted">{hint}</span>
      </span>
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Delete                                                                     */
/* -------------------------------------------------------------------------- */

export interface DeleteEntityDialogProps {
  target: TreeTarget;
  onClose: () => void;
}

/**
 * The confirmation for a cascading delete.
 *
 * The schema declares ON DELETE CASCADE the whole way down, so removing a
 * project removes its features and every task on them. A destructive action
 * that does not say what it is about to destroy is a trap, so the counts are
 * spelled out, they come from the same derivation the tree row printed, and
 * the button itself carries the total.
 */
export function DeleteEntityDialog({ target, onClose }: DeleteEntityDialogProps) {
  const mutation = useDeleteEntity();
  const totals = totalsFor(target);
  const kindLabel = ENTITY_LABELS[target.kind].toLowerCase();

  const lines: { count: number; label: string }[] = [];
  if (target.kind === 'client') lines.push({ count: totals.applications, label: 'application' });
  if (target.kind === 'client' || target.kind === 'application') {
    lines.push({ count: totals.projects, label: 'project' });
  }
  if (target.kind !== 'feature') lines.push({ count: totals.features, label: 'feature' });
  lines.push({ count: totals.tasks, label: 'task' });

  const doomed = lines.filter((line) => line.count > 0);

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={`Delete this ${kindLabel}?`}
      description={nameOf(target)}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant="danger"
            loading={mutation.isPending}
            onClick={() => mutation.mutate(target, { onSuccess: onClose })}
          >
            {totals.tasks > 0
              ? `Delete ${kindLabel} and ${formatCount(totals.tasks, 'task')}`
              : `Delete ${kindLabel}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mutation.error ? <ErrorNote error={mutation.error} /> : null}

        {doomed.length > 0 ? (
          <>
            <p className="text-sm text-muted">
              Deleting <span className="font-medium text-ink">{nameOf(target)}</span> also deletes
              everything underneath it. There is no undo.
            </p>
            <ul className="divide-y divide-line overflow-hidden rounded-md border border-danger/30 bg-danger-soft/50">
              {doomed.map((line) => (
                <li
                  key={line.label}
                  className="flex items-baseline justify-between gap-4 px-3.5 py-2"
                >
                  <span className="font-mono text-[1.25rem] leading-none text-danger-ink tnum">
                    {line.count}
                  </span>
                  <span className="text-[0.8125rem] text-muted">
                    {line.count === 1 ? line.label : `${line.label}s`}
                  </span>
                </li>
              ))}
            </ul>
            {totals.blocked > 0 ? (
              <p className="text-[0.8125rem] text-warn-ink">
                {formatCount(totals.blocked, 'task')} in there{' '}
                {totals.blocked === 1 ? 'is' : 'are'} currently blocked — somebody may be waiting on
                it.
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted">
            Nothing hangs off this {kindLabel}, so only the {kindLabel} itself goes. There is still
            no undo.
          </p>
        )}
      </div>
    </Dialog>
  );
}
